import { lookupToDropdownWithFallback } from "../../helpers/lookupHelpers";
import type {
  Diversions,
  RecyclingReport,
  RecyclingReportNew,
  RecyclingCategory,
  RecyclingCategoryType,
  RecyclingCounty,
  CountyMurn,
  MaterialCategory,
} from "@/views/recyclingReport/recyclingReport";
import { getLookupsFromLookupGroup, useLookup, type Lookup } from "@modernary/mui/components/lookup";
import { sortByAlphabeticalByField } from "@modernary/mui/utils/helpers";
import { ref, reactive } from "vue";
import { LE } from "@/constants/enumsNew";

export default {
  addAuthRequestFields,
  getInitialData,
  skinnyResponseAdapter,
  requestAdapter,
  filterToVisisble,
  responseAdapter,
};

const newRecyclingCounty = (countyMurn: string, allMaterialCategories: Lookup[]) => {
  const result: RecyclingCounty = {
    countyMurn: countyMurn as CountyMurn,
    totalTons: 0, //ref(0),
    materialCategories: new Array<RecyclingCategory>(),
    materialCategoriesMap: new Map<string, RecyclingCategory>(), // Only used while building the data, removed before returning.
    availableMaterialCategories: reactive<{ [key: string]: boolean }>({}),
    materialCategoriesArray: new Array<{ label: string; value: string }>(),
    hasAvailableCategories: ref(true),
    categorySelectionRef: ref(null),
  };
  //TODO:SYNC
  allMaterialCategories.forEach((x) => {
    result.availableMaterialCategories[x.murn] = true;
  });
  return result;
};

const newRecyclingCategory = (categoryMurn: MaterialCategory) => {
  return {
    categoryMurn,
    totalTons: 0, //ref(0),
    types: new Array<RecyclingCategoryType>(),
  };
};

const newDivertedCounty = (countyMurn: string) => {
  return {
    countyMurn: countyMurn as CountyMurn,
    totalTons: 0, //ref(0),
    materialCategories: new Array<{
      id: number;
      description: string;
      destination: string;
      tonnage: number;
      key: number;
    }>(),
  };
};

function addAuthRequestFields(entity: RecyclingReport) {
  //TODO:TS
  entity._authRequestFields = ["id", "organizationId", "year", "statusMurn", "typeMurn"] as (keyof RecyclingReport)[];
  return entity;
}

function getInitialData() {
  const result: RecyclingReport = {
    // type: "RecyclingReport",
    // period: "Annual",
    // status: "Draft",
    // diversions: { counties: [] } as unknown as Diversions, //TODO: TS
    // counties: [],
  } as unknown as RecyclingReport; //TODO: TS
  return addAuthRequestFields(result);
}

function getCountiesWithNullOption(counties: { label: string; value: string }[]) {
  // Just gives us a deep copy of the array at this point. Consider refactor.
  return counties.map((x) => ({ label: x.label, value: x.value }));
}

function filterToVisisble<T extends Array<{ label: string; value: string }>>(
  array: T,
  map: { [key: string]: boolean },
) {
  return array.filter((x) => map[x.value]);
}

/** Be sure to await this when using it. Commonly used for lists and child lists */
async function skinnyResponseAdapter<T extends RecyclingReport | RecyclingReportNew>(response: { data: T }) {
  return response.data;
}

/** Be sure to await this when using it. */
async function responseAdapter<T extends RecyclingReport | RecyclingReportNew>(response: { data: T }) {
  await skinnyResponseAdapter(response);

  const allMaterialCategories = await getLookupsFromLookupGroup("MaterialCategory").then((x) => x._items);

  const countyLookup = useLookup("County");
  await countyLookup.loadOptions({ stateMurn: LE.State.NV }, undefined); //prefilter for NV
  const allCounties = countyLookup.options;

  const allMaterialCategoriesArray = lookupToDropdownWithFallback(allMaterialCategories);
  const availableCountiesArray = [...countyLookup.options.value];

  // Materials come to us completely flattened. We need to build a hierarchy for the UI.
  let countyMap: { [key: string]: RecyclingCounty } = {};
  let counties: RecyclingCounty[] = [];
  const rollUp: RecyclingReport["rollUp"] = {
    totalTons: 0,
    categories: {},
  };
  //TODO:SYNC
  // const availableCountiesArray = getCountiesWithNullOption();
  const availableCounties: { [key: string]: boolean } = {};
  //TODO:SYNC
  availableCountiesArray.forEach((x) => (availableCounties[x.value] = true));
  if (isFullRecyclingReport(response.data) && response.data.recycledMaterials) {
    response.data.recycledMaterials.forEach((item) => {
      let county = countyMap[item.countyMurn];
      if (!county) {
        county = countyMap[item.countyMurn] = newRecyclingCounty(item.countyMurn, allMaterialCategories);
        counties.push(county);
      }
      availableCounties[county.countyMurn] = false;

      let category = county.materialCategoriesMap?.get(item.categoryMurn);
      if (!category) {
        category = newRecyclingCategory(item.categoryMurn);
        county.materialCategoriesMap?.set(item.categoryMurn, category);
        county.availableMaterialCategories[category.categoryMurn] = false;
        county.materialCategories.push(category);
      }
      category.types.push({
        id: item.id,
        destination: item.destination,
        companyName: item.companyName,
        streetAddress: item.streetAddress,
        city: item.city,
        stateMurn: item.stateMurn,
        countryMurn: item.countryMurn,
        tonnage: item.tonnage,
        typeMurn: item.typeMurn,
        key: item.id,
        isDuplicated: item.isDuplicated,
      });

      let categoryRollUp = rollUp.categories[item.categoryMurn];
      if (!categoryRollUp) {
        categoryRollUp = rollUp.categories[item.categoryMurn] = {
          categoryMurn: item.categoryMurn,
          category: item.categoryMurn ? allMaterialCategories.find((amc) => amc.murn === item.categoryMurn) : undefined,
          totalTons: 0,
        };
      }

      category!.totalTons += item.tonnage;
      county.totalTons += item.tonnage;
      rollUp.totalTons += item.tonnage;
      categoryRollUp.totalTons += item.tonnage;
    });
  }

  counties.forEach((county) => {
    //TODO:SYNC
    county.materialCategoriesArray = filterToVisisble(allMaterialCategoriesArray, county.availableMaterialCategories);
    county.hasAvailableCategories.value = county.materialCategoriesArray.length > 0; // hasVisibleOption(county.availableMaterialCategories);
    delete county.materialCategoriesMap;
  });

  sortByAlphabeticalByField(counties, "county");

  response.data.rollUp = rollUp;
  response.data.counties = counties;

  // Reset vars.

  const diversions: Diversions = {
    totalTons: 0,
    countyMap: {},
    counties: [],
    //TODO:SYNC
    availableCountiesArray: getCountiesWithNullOption(allCounties.value),
    availableCounties: {},
  };
  //TODO:SYNC
  diversions.availableCountiesArray.forEach((x) => (diversions.availableCounties[x.value] = true));

  let countyMapForDiversions = diversions.countyMap;
  let countiesForDiversions = diversions.counties;
  if (isFullRecyclingReport(response.data)) {
    response.data.divertedMaterials?.forEach((item) => {
      let countyMurn = countyMapForDiversions[item.countyMurn];
      if (!countyMurn) {
        countyMurn = countyMapForDiversions[item.countyMurn] = newDivertedCounty(item.countyMurn);
        countiesForDiversions.push(countyMurn);
        diversions.availableCounties[countyMurn.countyMurn] = false;
      }
      countyMurn.materialCategories.push({
        id: item.id,
        description: item.description,
        destination: item.destination,
        tonnage: item.tonnage,
        key: item.id,
      });
      countyMurn.totalTons += item.tonnage;
      diversions.totalTons += item.tonnage;
    });
  }

  sortByAlphabeticalByField(diversions.counties, "county");

  response.data.diversions = diversions;
  response.data.countiesMap = availableCounties;
  //TODO:SYNC
  response.data.allCounties = availableCountiesArray;
  response.data.availableCountiesArray = availableCountiesArray.filter((x) => availableCounties[x.value]);
  response.data.allMaterialCategoriesArray = allMaterialCategoriesArray;

  response.data.newRecyclingCounty = newRecyclingCounty;
  response.data.newRecyclingCategory = newRecyclingCategory;
  response.data.newDivertedCounty = newDivertedCounty;

  if (isFullRecyclingReport(response.data)) {
    addAuthRequestFields(response.data);
  }

  return response.data;
}

function isFullRecyclingReport(
  recyclingReport: RecyclingReport | RecyclingReportNew,
): recyclingReport is RecyclingReport {
  return (recyclingReport as RecyclingReport).id !== undefined;
}

function requestAdapter() {}
